import { createRng } from '../../../core/rng';
import { Grid, hits } from '../song';
import type { Note } from '../song';
import { barsOf, cellsOf, chooser, cycle, find, fitForm, midi, noteName, pcs, phrase, sizeOf, titleOf, totalBars, trackId, transpose } from '../score';
import type { Ev, FormPlan, Params, Score, Template } from '../score';

type Chord = { root: string; stab: readonly string[]; harm: readonly number[] };

const CHORDS: Readonly<Record<string, Chord>> = {
  C: { root: 'C2', stab: ['E4', 'G4', 'C5', 'E5'], harm: [0, 4, 7] },
  F: { root: 'F2', stab: ['F4', 'A4', 'C5', 'F5'], harm: [5, 9, 0] },
  G7: { root: 'G2', stab: ['F4', 'G4', 'B4', 'D5'], harm: [7, 11, 2, 5] },
  A7: { root: 'A2', stab: ['E4', 'G4', 'C#5', 'E5'], harm: [9, 1, 4, 7] },
  Dm: { root: 'D2', stab: ['F4', 'A4', 'D5', 'F5'], harm: [2, 5, 9] },
  D7: { root: 'D2', stab: ['F#4', 'A4', 'C5', 'D5'], harm: [2, 6, 9, 0] },
  E7: { root: 'E2', stab: ['E4', 'G#4', 'B4', 'D5'], harm: [4, 8, 11, 2] },
  Am: { root: 'A2', stab: ['E4', 'A4', 'C5', 'E5'], harm: [9, 0, 4] },
};

const PROGS = [
  ['C', 'G7', 'G7', 'C'],
  ['C', 'A7', 'Dm', 'G7'],
  ['C', 'C', 'D7', 'G7'],
  ['Am', 'E7', 'E7', 'Am'],
  ['C', 'F', 'G7', 'C'],
] as const;

const HOOK: Note[][] = [
  [[0, 'G4', 1], [1, 'C5', 1], [2, 'E5', 2], [4, 'D5', 1, 0.8], [5, 'C5', 1, 0.8], [6, 'G4', 2], [8, 'E4', 1], [9, 'G4', 1], [10, 'C5', 2], [12, 'B4', 1, 0.8], [13, 'C5', 1, 0.8], [14, 'D5', 2]],
  [[0, 'F5', 2], [2, 'D5', 2], [4, 'B4', 1, 0.8], [5, 'G4', 1, 0.8], [6, 'F4', 2], [8, 'G4', 1], [9, 'B4', 1], [10, 'D5', 2], [12, 'F5', 2], [14, 'E5', 1, 0.8], [15, 'D5', 1, 0.8]],
  [[0, 'B4', 2], [2, 'D5', 1], [3, 'B4', 1], [4, 'G4', 2], [6, 'A4', 1, 0.8], [7, 'B4', 1, 0.8], [8, 'D5', 3], [11, 'B4', 1, 0.7], [12, 'G4', 2], [14, 'F4', 2]],
  [[0, 'E4', 2], [2, 'G4', 2], [4, 'C5', 4], [8, 'E5', 4], [12, 'C5', 2, 0.9], [14, 'G4', 2, 0.8]],
];

const TUBAS: readonly (readonly [step: number, semis: number, len: number][])[] = [
  [[0, 0, 3.4], [4, 7, 3.4], [8, 0, 3.4], [12, 7, 1.7]],
  [[0, 0, 1.7], [2, 0, 1.6], [4, 7, 3.4], [8, 0, 1.7], [10, 0, 1.6], [12, 7, 1.7]],
  [[0, 0, 3.4], [4, 12, 1.7], [6, 7, 1.7], [8, 0, 3.4], [12, 4, 1.7]],
];

const STABS: readonly (readonly [step: number, len: number, vel: number][])[] = [
  [[2, 1, 0.85], [6, 2, 1], [10, 1, 0.85], [14, 2, 1]],
  [[0, 2, 1], [3, 1, 0.7], [6, 2, 0.95], [10, 1, 0.85], [12, 2, 1]],
  [[2, 1, 0.9], [4, 1, 0.75], [6, 2, 1], [10, 1, 0.9], [12, 1, 0.75], [14, 2, 1]],
];

const RAJADAS: typeof STABS = [
  [[0, 1, 1], [2, 1, 0.8], [4, 1, 0.9], [8, 1, 1], [10, 1, 0.8], [12, 1, 0.9], [14, 1, 0.85]],
  [[0, 2, 1], [4, 1, 0.85], [6, 1, 0.8], [8, 2, 1], [12, 1, 0.85], [14, 1, 0.9]],
  [[0, 1, 1], [4, 1, 0.9], [6, 2, 0.95], [8, 1, 1], [12, 1, 0.9], [14, 2, 1]],
];

const KEYS = [0, -2, 3, 5, -4, 2] as const;
const MAJOR = [0, 2, 4, 5, 7, 9, 11] as const;
const SURDOS = ['X...X...X...X..x', 'X...X..xX...X...', 'X..xX...X..xX...'] as const;
const TAROLS = ['o-oxo-oxo-oxo-ox', 'o-o-x-oxo-o-x-ox', 'oxo-o-xoo-oxo-xo'] as const;
const CAIXA = '....X.......X...';
const CAIXA_DROP = '....X.....x.X...';
const PRATOS = '....x.......x...';

const PLAN: FormPlan = {
  intro: 4,
  outro: 4,
  body: [
    { name: 'A', bars: 8, min: 4, rank: 2 },
    { name: 'drop', bars: 8, min: 4, rank: 1 },
    { name: 'break', bars: 4, min: 4, rank: 4, with: 'drop2' },
    { name: 'drop2', bars: 8, min: 4, rank: 3 },
  ],
};

function under(note: string, harm: readonly number[]): string {
  const m = midi(note);
  for (let d = 3; d <= 9; d++) if (harm.includes((((m - d) % 12) + 12) % 12)) return noteName(m - d);
  return noteName(m - 3);
}

function score(seed: string, p: Params): Score {
  const ch = chooser(FREVO, seed);
  const key = ch.pick(KEYS);
  const prog = ch.pick(PROGS).map((n) => CHORDS[n]!);
  const surdoP = ch.pick(SURDOS);
  const tarolP = ch.pick(TAROLS);
  const tubaRow = ch.pick(TUBAS);
  const stabRow = ch.pick(p.energy >= 0.7 ? RAJADAS : STABS);
  const hook = ch.orig
    ? HOOK
    : phrase(
        ch.rng,
        HOOK.map(cellsOf),
        prog.map((c) => [{ at: 0, tones: pcs([c.root, ...c.stab]) }]),
        MAJOR,
        [64, 79],
        72,
      );
  const e = p.energy;
  const tr = (n: string) => transpose(n, key);
  const low = (n: string) => {
    let m = midi(tr(n));
    while (m < 33) m += 12;
    while (m > 45) m -= 12;
    return noteName(m);
  };
  const harmOf = (c: Chord) => c.harm.map((x) => (x + key + 120) % 12);

  const form = fitForm(barsOf(p), PLAN);
  const g = new Grid(p.bpm, 50, form);
  const s = g.step;
  const intro = form[0]!.bars;
  const total = totalBars(form);
  const drop1 = find(form, 'drop')!;
  const drop2 = find(form, 'drop2');
  const brk = find(form, 'break');
  const groove1 = find(form, 'A');
  const rng = createRng(`frevo-${seed}`);
  const human = (v: number) => v * (0.92 + rng.float() * 0.16);

  const events: Ev[] = [];
  const hit = (v: string, bar: number, step: number, vel: number) => events.push({ v, t: g.at(bar, step), bar, step, vel });
  const play = (v: string, bar: number, step: number, note: string, dur: number, vel: number) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, note });
  const chord = (v: string, bar: number, step: number, notes: readonly string[], dur: number, vel: number, strum = 0) =>
    events.push({ v, t: g.at(bar, step), bar, step, vel, dur, notes, strum });
  const rufo = (bar: number, from: number) => {
    for (let st = from; st < 16; st += 0.5) hit('tarol', bar, st, 0.3 + (0.65 * (st - from)) / (16 - from));
  };
  const lastBefore = (bar: number) => bar === drop1 - 1 || (drop2 !== undefined && bar === drop2 - 1);

  const autos: Score['autos'] = [
    { v: 'brassLp', points: [[g.at(0), 2200], [g.at(intro), 7000]] },
    { v: 'brassLp', points: [[g.at(total - 1), 7000], [g.at(total), 2400]] },
  ];

  for (const b of g.bars()) {
    const i = cycle(b.bar - intro, 4);
    const end = b.bar === total - 1;
    const c = end ? prog[0]! : prog[i]!;
    const next = prog[cycle(b.bar - intro + 1, 4)]!;
    const drop = b.name === 'drop' || b.name === 'drop2';
    const band = drop || b.name === 'A' || (b.name === 'outro' && !end);
    const stab = c.stab.map(tr);
    const roll = lastBefore(b.bar);

    if (end) {
      hit('surdo', b.bar, 0, 1);
      hit('caixa', b.bar, 0, 0.9);
      hit('crash', b.bar, 0, 0.9);
      hit('impact', b.bar, 0, 0.8);
      chord('stab', b.bar, 0, stab, s * 6, 1);
      chord('bone', b.bar, 0, stab.slice(0, 2).map((n) => transpose(n, -12)), s * 6, 0.9);
      play('tuba', b.bar, 0, low(c.root), s * 6, 0.95);
      play('sub', b.bar, 0, low(c.root), s * 8, 0.9);
      continue;
    }

    if (band) {
      for (const h of hits(surdoP)) hit('surdo', b.bar, h.step, human(h.vel));
      for (const h of hits(drop ? CAIXA_DROP : CAIXA)) hit('caixa', b.bar, h.step, human(h.vel));
      if (roll) rufo(b.bar, 8);
      const tarolUntil = roll ? 8 : 16;
      if (drop || e >= 0.75) for (const h of hits(tarolP)) if (h.step < tarolUntil) hit('tarol', b.bar, h.step, human(h.vel * (drop ? 1 : 0.8)));
      if (!drop && e < 0.75) for (const h of hits('o.o.o.o.o.o.o.o.')) if (h.step < tarolUntil) hit('tarol', b.bar, h.step, human(h.vel));
      for (const h of hits(PRATOS)) hit('prato', b.bar, h.step, human(drop ? 0.9 : 0.6));
      const root = midi(low(c.root));
      for (const [step, semis, len] of tubaRow) {
        const note = noteName(root + semis);
        play('tuba', b.bar, step, note, len * s * 0.92, human(0.9));
        play('sub', b.bar, step, note, len * s * 0.92, 0.8);
      }
      const target = midi(low(next.root));
      const approach = noteName(target === root ? root + 7 : target - 1);
      play('tuba', b.bar, 14, approach, s * 1.7, human(0.85));
      play('sub', b.bar, 14, approach, s * 1.7, 0.75);
      const sv = drop ? 1 : 0.75;
      for (const [step, len, vel] of stabRow) {
        chord('stab', b.bar, step, stab, len * s * 0.9, human(vel * sv));
        if (drop) chord('bone', b.bar, step, stab.slice(0, 2).map((n) => transpose(n, -12)), len * s * 0.9, human(vel * 0.9));
      }
    } else if (b.name === 'break') {
      for (const h of hits('X.......X.......')) hit('surdo', b.bar, h.step, human(0.95));
      for (const h of hits(CAIXA)) hit('caixa', b.bar, h.step, human(0.8));
      if (b.last) rufo(b.bar, 8);
      else for (const h of hits('o.o.o.o.o.o.o.o.')) hit('tarol', b.bar, h.step, human(h.vel * 0.8));
      play('sub', b.bar, 0, low(c.root), s * 14, 0.65);
      chord('bone', b.bar, 0, stab.slice(0, 3).map((n) => transpose(n, -12)), g.barLength * 0.92, 0.7);
      if (e >= 0.7) for (const st of b.last ? [0, 4] : [0, 4, 8, 12]) chord('stab', b.bar, st, stab, s * 0.9, human(0.6));
    } else if (b.name === 'intro') {
      if (!b.last) for (const h of hits('o.o.o-o.o.o.o-o.')) hit('tarol', b.bar, h.step, human(h.vel));
      if (b.index >= 1) for (const h of hits(PRATOS)) hit('prato', b.bar, h.step, human(0.5));
      if (b.last) {
        rufo(b.bar, 0);
        chord('stab', b.bar, 14, stab, s * 1.8, 0.9);
      }
    }

    const row = hook[i]!;
    const melody = (v: string, vel: number, shift: number) => {
      for (const [step, note, len, nv] of row) play(v, b.bar, step, transpose(tr(note), shift), len * s * 0.88, human((nv ?? 0.95) * vel));
    };
    const harmony = (vel: number) => {
      for (const [step, note, len, nv] of row) play('saxHarm', b.bar, step, under(tr(note), harmOf(c)), len * s * 0.86, (nv ?? 0.9) * vel);
    };
    if (b.name === 'A') melody('sax', 0.9, 0);
    if (b.name === 'intro' && b.index >= b.bars / 2 && !b.last) melody('sax', 0.6, 0);
    if (drop) {
      melody('lead', 1, 0);
      melody('sax', 0.6, -12);
      harmony(0.55);
      if (b.name === 'drop2' && e >= 0.85) melody('lead', 0.4, 12);
    }
    if (b.name === 'break' && b.index < b.bars / 2) melody('sax', 0.7, 0);
  }

  events.push({ v: 'rise', t: g.at(Math.max(intro, drop1 - 2)), end: g.at(drop1) - s * 2, bar: Math.max(intro, drop1 - 2), step: 0, vel: 1 });
  if (drop2 !== undefined && brk !== undefined) {
    const from = brk + sizeOf(form, 'break') / 2;
    events.push({ v: 'rise', t: g.at(from), end: g.at(drop2) - s * 2, bar: from, step: 0, vel: 1 });
    events.push({ v: 'fall', t: g.at(brk), end: g.at(brk + 1), bar: brk, step: 0, vel: 1 });
  }
  for (const bar of [drop1, drop2]) {
    if (bar === undefined) continue;
    hit('crash', bar, 0, 1);
    hit('impact', bar, 0, 1);
  }
  if (groove1 !== undefined) hit('crash', groove1, 0, 0.6);

  return {
    id: trackId(FREVO, seed, p),
    template: FREVO.id,
    seed,
    params: p,
    title: ch.orig ? FREVO.ref.title : titleOf(seed),
    genre: FREVO.genre,
    bpm: p.bpm,
    swing: 50,
    lufs: -10.5,
    key,
    form,
    mix: { reverbDecay: 1.5, delayTime: (60 / p.bpm) * 0.75, delayFeedback: 0.15, drive: 1.6, softness: 1.2, pumpDepth: 0.22, pumpRelease: 0.16 },
    events,
    autos,
    accents: { surdo: 'kick', caixa: 'snare', stab: 'stab', lead: 'melody', sax: 'melody', impact: 'drop' },
  };
}

export const FREVO: Template = {
  id: 'frevo',
  genre: 'frevo de rua',
  bpm: [128, 144],
  ref: { seed: 'passo', title: 'Passo', params: { bpm: 132, bars: 32, energy: 0.65 } },
  score,
};
